import { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  OpsTatPath,
  OpsTatStatus,
} from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { OpsTatStageCard } from "../../../../../../models/Dashboard/OpsTat/OpsTatSummary";

export interface OpsTatStatusColors {
  fg: string;
  bg: string;
}

export interface OpsTatSparklinePoint {
  x: number;
  y: number;
}

export interface OpsTatSparklineLayout {
  segments: OpsTatSparklinePoint[][];
  lastPoint: OpsTatSparklinePoint | null;
}

const EMPTY_VALUE = "—";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const formatOpsTatNumber = (
  value: number | null | undefined,
  digits = 1,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    return value.toFixed(digits);
  } catch (error) {
    return EMPTY_VALUE;
  }
};

/** Raw decimal days → `1.8d`. Never converts to `1d 19h`. */
export const formatOpsTatDays = (
  value: number | null | undefined,
  digits = 1,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    return `${formatOpsTatNumber(value, digits)}d`;
  } catch (error) {
    return EMPTY_VALUE;
  }
};

export const formatOpsTatSignedDays = (
  value: number | null | undefined,
  digits = 1,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    const sign = value > 0 ? "+" : "";
    return `${sign}${formatOpsTatDays(value, digits)}`;
  } catch (error) {
    return EMPTY_VALUE;
  }
};

export const formatOpsTatDeltaLabel = (
  value: number | null | undefined,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    if (value === 0) {
      return "on SLA";
    }
    const signed = formatOpsTatSignedDays(value);
    return value > 0 ? `${signed} over SLA` : `${signed} under SLA`;
  } catch (error) {
    return EMPTY_VALUE;
  }
};

export const formatOpsTatPercent = (
  value: number | null | undefined,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    return `${Math.round(value)}%`;
  } catch (error) {
    return EMPTY_VALUE;
  }
};

export const formatOpsTatCount = (value: number | null | undefined): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    return value.toLocaleString("en-IN");
  } catch (error) {
    return EMPTY_VALUE;
  }
};

const isCountStatLabel = (label?: string): boolean => {
  if (!label) {
    return false;
  }
  return /^(cycles|orders|n|count)$/i.test(label.trim());
};

/** Use `stats[].unit` as-is (`d` | `%` | `""`). Counts keep the API sign only. */
export const formatOpsTatStatValue = (
  value: number | null | undefined,
  unit: string,
  label?: string,
): string => {
  try {
    if (!isFiniteNumber(value)) {
      return EMPTY_VALUE;
    }
    if (unit === "" || isCountStatLabel(label)) {
      return formatOpsTatCount(value);
    }
    if (unit === "d" || unit === "days") {
      return formatOpsTatDays(value);
    }
    if (unit === "%") {
      return formatOpsTatPercent(value);
    }
    return `${formatOpsTatNumber(value)}${unit}`;
  } catch (error) {
    return EMPTY_VALUE;
  }
};

export const formatOpsTatAsOf = (iso: string | null | undefined): string => {
  try {
    if (!iso) {
      return "";
    }
    const parsed = dayjs(iso);
    if (!parsed.isValid()) {
      return "";
    }
    return `as of ${parsed.format("D MMM YYYY, HH:mm")}`;
  } catch (error) {
    return "";
  }
};

export const formatOpsTatPathEyebrow = (path: OpsTatPath): string => {
  try {
    if (path === "custom") {
      return "custom path";
    }
    if (path === "stock") {
      return "stock path";
    }
    return "all paths";
  } catch (error) {
    return "all paths";
  }
};

export const getOpsTatStatusColors = (
  theme: Theme,
  status: OpsTatStatus | "clay" | "amber" | undefined,
): OpsTatStatusColors => {
  try {
    const customColors = theme.palette.customColors;
    if (status === "ok") {
      return {
        fg: customColors.successGreen,
        bg: customColors.successGreenLightBackground,
      };
    }
    if (status === "warn" || status === "amber") {
      return {
        fg: theme.palette.goldenYellow.main,
        bg: customColors.lightOrange,
      };
    }
    if (status === "bad" || status === "clay") {
      return {
        fg: customColors.errorRed,
        bg: customColors.lightred,
      };
    }
    return {
      fg: customColors.greyTextColor,
      bg: customColors.veryLightGray,
    };
  } catch (error) {
    return {
      fg: theme.palette.customColors.greyTextColor,
      bg: theme.palette.customColors.veryLightGray,
    };
  }
};

export const getOpsTatStatusPillSx = (
  theme: Theme,
  status: OpsTatStatus | "clay" | "amber" | undefined,
) => {
  const colors = getOpsTatStatusColors(theme, status);
  return {
    display: "inline-flex",
    alignItems: "center",
    px: 1,
    py: 0.25,
    borderRadius: 1,
    bgcolor: colors.bg,
    color: colors.fg,
  };
};

/**
 * Shared scale for the dual-track spine. Visual only — never use this
 * for the hero cycle (that is `headline.cycle` only).
 */
export const getOpsTatSpineScale = (stages: OpsTatStageCard[]): number => {
  try {
    const actualTotal = stages.reduce((sum, stage) => {
      const mean = stage.actual?.mean;
      return sum + (isFiniteNumber(mean) ? mean : 0);
    }, 0);
    const targetTotal = stages.reduce((sum, stage) => {
      return sum + (isFiniteNumber(stage.target) ? stage.target : 0);
    }, 0);
    return Math.max(actualTotal, targetTotal, 0.001);
  } catch (error) {
    return 0.001;
  }
};

/** Pixel floor. Stages expand from this using actual / SLA days. Visual only. */
export const OPS_TAT_SPINE_MIN_PX = 40;

/** Extra pixels per day so 2.1d is clearly wider than 0.2d. Visual only. */
export const OPS_TAT_SPINE_PX_PER_DAY = 28;

const toSpineDays = (days: number | null | undefined): number =>
  isFiniteNumber(days) && days > 0 ? days : 0;

/** Flex grow from days, 40px basis. Zero-day stages stay at 40px. */
export const getOpsTatSpineFlex = (days: number | null | undefined): string => {
  try {
    return `${toSpineDays(days)} 0 ${OPS_TAT_SPINE_MIN_PX}px`;
  } catch (error) {
    return `0 0 ${OPS_TAT_SPINE_MIN_PX}px`;
  }
};

/** Track min-width so mobile can scroll instead of compressing stages. */
export const getOpsTatSpineTrackMinWidth = <T,>(
  stages: T[],
  getDays: (stage: T) => number | null | undefined,
): number => {
  try {
    if (stages.length === 0) {
      return 0;
    }
    const extras = stages.reduce((sum, stage) => {
      return sum + toSpineDays(getDays(stage)) * OPS_TAT_SPINE_PX_PER_DAY;
    }, 0);
    const gaps = Math.max(stages.length - 1, 0) * 2;
    return stages.length * OPS_TAT_SPINE_MIN_PX + extras + gaps;
  } catch (error) {
    return stages.length * OPS_TAT_SPINE_MIN_PX;
  }
};

/**
 * Sparkline points from `trend`. Null buckets become gaps.
 * Do not interpolate across missing weeks.
 */
export const buildOpsTatSparklineSegments = (
  trend: Array<number | null>,
  width: number,
  height: number,
): OpsTatSparklineLayout => {
  try {
    const values = Array.isArray(trend) ? trend : [];
    const numeric = values.filter(isFiniteNumber);
    if (numeric.length === 0 || width <= 0 || height <= 0) {
      return { segments: [], lastPoint: null };
    }

    const min = Math.min(...numeric);
    const max = Math.max(...numeric);
    const range = max - min || 1;
    const padY = 3;
    const lastIndex = Math.max(values.length - 1, 1);

    const toPoint = (value: number, index: number): OpsTatSparklinePoint => ({
      x: values.length === 1 ? width / 2 : (index / lastIndex) * width,
      y: height - padY - ((value - min) / range) * (height - padY * 2),
    });

    const segments: OpsTatSparklinePoint[][] = [];
    let current: OpsTatSparklinePoint[] = [];
    let lastPoint: OpsTatSparklinePoint | null = null;

    values.forEach((value, index) => {
      if (!isFiniteNumber(value)) {
        if (current.length > 0) {
          segments.push(current);
          current = [];
        }
        return;
      }
      const point = toPoint(value, index);
      current.push(point);
      lastPoint = point;
    });

    if (current.length > 0) {
      segments.push(current);
    }

    return { segments, lastPoint };
  } catch (error) {
    return { segments: [], lastPoint: null };
  }
};

export const toOpsTatSparklinePath = (
  points: OpsTatSparklinePoint[],
): string => {
  try {
    if (points.length === 0) {
      return "";
    }
    return points
      .map((point, index) => {
        const command = index === 0 ? "M" : "L";
        return `${command}${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
      })
      .join(" ");
  } catch (error) {
    return "";
  }
};

export const toOpsTatSparklineArea = (
  points: OpsTatSparklinePoint[],
  height: number,
): string => {
  try {
    if (points.length < 2) {
      return "";
    }
    const line = toOpsTatSparklinePath(points);
    const last = points[points.length - 1];
    const first = points[0];
    return `${line} L${last.x.toFixed(1)} ${height} L${first.x.toFixed(1)} ${height} Z`;
  } catch (error) {
    return "";
  }
};
